import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from '../../utils/validators';
import ConfirmModal from '../../components/confirmModal';
import {
    ToastContext,
    WeezHeader,
    Form,
    Card,
    Counter,
    SelectLabel,
    ToggleCard,
    ToggleContainer,
    Message,
    OptionalSection,
    Input,
    Section,
    Subsection,
    Button,
    TextAreaLabel,
    Icon,
    TooltipHelp
} from '@weezevent/nacre';
import EventSettingsModel from './../../models/eventSettings';
import InvoicingModel from './../../models/invoicingModel';
import Style from './payement-and-invoicing.module.css';
import Sofort from './images/sofort.png';
import Paypal from './images/paypal.png';
import Klarna from './images/klarna.png';
import Visa from './images/visa.png';
import MasterCard from './images/mastercard.png';
import Amex from './images/amex.png';
import EventCommissionRules from '../../models/eventCommissionRules';

export default function PayementAndInvoicing({ organization, event }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [isSubmitting, setIsSubmitting] = React.useState();
    const [modalConfirm, setModalConfirm] = React.useState(null);
    const [paymentForm, setPaymentForm, setPaymentFormObject] = useForm({
        paiement_online: true,
        accepte_amex: false,
        sofort_enabled: false,
        paypal_enabled: false,
        klarna_enabled: false,
        paiement_offline: false,
        accepte_remboursement: false,
        limit_tickets_per_order: 0,
        instructions_remboursement: '',
        instructions_paiement_place: '',
        proforma_enabled: false
    });
    const [showMorePayments, setShowMorePayments] = React.useState(false);
    const [invoicingForm, setInvoiceForm, setInvoiceFormObject] = useForm({
        etat: '', // 0 : Disabled, 1: Optional, 2:Mandatory
        etat_hl: '0', // Corresponds to Specific coordinates toggle
        societe: '',
        forme: '',
        siret: '',
        siret_lieu: '',
        tva_intra: '',
        adresse: '',
        cp: '',
        province: '',
        ville: '',
        pays: '',
        divers: '',
        telephone: '',
        email: ''
    });

    const [eventSettings, , sync] = EventSettingsModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            cache: false,
            allow_cache: false,
            onSuccess: settings => {
                const values = settings.values;
                let formatedValues = {
                    paiement_online: Boolean(parseInt(values.paiement_online)),
                    accepte_amex: Boolean(parseInt(values.accepte_amex)),
                    sofort_enabled: Boolean(parseInt(values.sofort_enabled)),
                    paypal_enabled: Boolean(parseInt(values.paypal_enabled)),
                    klarna_enabled: Boolean(parseInt(values.klarna_enabled)),
                    paiement_offline: Boolean(parseInt(values.paiement_offline)),
                    accepte_remboursement: Boolean(parseInt(values.accepte_remboursement)),
                    limit_tickets_per_order: parseInt(values.limit_tickets_per_order),
                    proforma_enabled: Boolean(parseInt(values.proforma_enabled))
                };
                setPaymentFormObject({ ...values, ...formatedValues });
                setShowMorePayments(formatedValues.accepte_amex || formatedValues.sofort_enabled || formatedValues.paypal_enabled || formatedValues.klarna_enabled);
            }
        },
        [organization, event]
    );

    const [eventCommissionRules] = EventCommissionRules.useApiModel({
        organizationId: organization.pk,
        eventId: event.pk,
        query: {
            card_types: [1, 2, 3, 4]
        }
    });

    const typeMapping = ['cb', 'amex', 'sofort', 'klarna', 'paypal'];
    const currencyMapping = {
        EUR: '€',
        CAD: '$',
        CHF: 'CHF',
        USD: '$',
        GBP: '£'
    };

    const eventCommissionByType = React.useCallback(
        paymentName => {
            if (!eventCommissionRules) {
                return null;
            }
            const paymentType = typeMapping.indexOf(paymentName);
            const rule = eventCommissionRules.find(rule => rule.card_type === paymentType);
            const logoCurrency = currencyMapping[event.currency];
            if (!rule) {
                return null;
            }
            const fixed = Number(rule.fixed) > 0 ? `${rule.fixed}${logoCurrency} + ` : '';
            const fixedAndRate = { rate: `${fixed}${rule.rate}\n%\n`, min: `${rule.min}${logoCurrency}` };
            return t(`paymentinvoicing.payment_card.more_payments.${paymentName}.modal_content`, fixedAndRate);
        },
        [eventCommissionRules]
    );

    InvoicingModel.useApiModel({
        organizationId: organization.pk,
        eventId: event.pk,
        onSuccess: invoice => {
            setInvoiceFormObject({ ...invoice }, true);
            setInvoiceForm('etat_hl', invoice.etat_hl === '' ? '0' : invoice.etat_hl);
        }
    });
    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        let settings = new EventSettingsModel({
            id: eventSettings.id,
            values: {
                ...paymentForm,
                paiement_online: paymentForm.paiement_online ? '1' : '0',
                paiement_offline: paymentForm.paiement_offline ? '1' : '0',
                accepte_amex: paymentForm.accepte_amex ? '1' : '0',
                sofort_enabled: paymentForm.sofort_enabled ? '1' : '0',
                paypal_enabled: paymentForm.paypal_enabled ? '1' : '0',
                klarna_enabled: paymentForm.klarna_enabled ? '1' : '0',
                accepte_remboursement: paymentForm.accepte_remboursement ? '1' : '0',
                limit_tickets_per_order: paymentForm.limit_tickets_per_order,
                instructions_remboursement: paymentForm.instructions_remboursement,
                instructions_paiement_place: paymentForm.instructions_paiement_place,
                proforma_enabled: paymentForm.proforma_enabled ? '1' : '0'
            }
        });
        settings
            .save({ organizationId: organization.pk, eventId: event.pk })
            .then(() => {
                let invoicing = new InvoicingModel({
                    id_ev: event.id,
                    id_guichet: 0,
                    ...invoicingForm
                });
                invoicing
                    .save({ organizationId: organization.pk, eventId: event.pk })
                    .then(() => {
                        toast.success(t('paymentinvoicing.save.success'));
                    })
                    .catch(e => {
                        console.error(e);
                        toast.error(t('paymentinvoicing.save.error'));
                    });
                sync();
            })
            .catch(err => {
                console.error(err);
                toast.error(t('paymentinvoicing.save.error'));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [organization, event, sync, eventSettings, paymentForm, invoicingForm]);

    const paymentChoices = React.useMemo(() => {
        return [
            {
                key: 'accepte_amex',
                logo: Amex,
                title: t('paymentinvoicing.payment_card.more_payments.amex.toggle_title'),
                subtitle: t('paymentinvoicing.payment_card.more_payments.amex.toggle_subtitle'),
                confirm: true,
                confirmModal: {
                    title: t('paymentinvoicing.payment_card.more_payments.amex.modal_title'),
                    content: eventCommissionByType('amex')
                }
            },
            {
                key: 'sofort_enabled',
                logo: Sofort,
                title: t('paymentinvoicing.payment_card.more_payments.sofort.toggle_title'),
                subtitle: t('paymentinvoicing.payment_card.more_payments.sofort.toggle_subtitle'),
                confirm: true,
                confirmModal:
                    event.currency === 'CHF'
                        ? {
                              title: t('paymentinvoicing.payment_card.more_payments.sofort.modal_title'),
                              content: eventCommissionByType('sofort')
                          }
                        : null
            },
            {
                key: 'paypal_enabled',
                logo: Paypal,
                title: t('paymentinvoicing.payment_card.more_payments.paypal.toggle_title'),
                subtitle: t('paymentinvoicing.payment_card.more_payments.paypal.toggle_subtitle'),
                confirm: true,
                confirmModal: {
                    title: t('paymentinvoicing.payment_card.more_payments.paypal.modal_title'),
                    content: eventCommissionByType('paypal')
                }
            },
            {
                key: 'klarna_enabled',
                logo: Klarna,
                title: t('paymentinvoicing.payment_card.more_payments.klarna.toggle_title'),
                subtitle: t('paymentinvoicing.payment_card.more_payments.klarna.toggle_subtitle'),
                confirm: true,
                confirmModal: {
                    title: t('paymentinvoicing.payment_card.more_payments.klarna.modal_title'),
                    content: eventCommissionByType('klarna')
                }
            }
        ];
    }, [eventCommissionByType]);

    const filteredPaymentChoices = React.useMemo(() => {
        // Hide payment methods not available for the currency set
        if (event.currency === 'USD') {
            return paymentChoices.filter(p => p.key !== 'paypal_enabled');
        }
        if (event.currency !== 'CHF') {
            return paymentChoices.filter(p => p.key !== 'sofort_enabled');
        }
        if (event.currency !== 'EUR') {
            return paymentChoices.filter(p => p.key !== 'klarna_enabled');
        }
        return paymentChoices;
    }, [event, paymentChoices]);

    const toggleChange = React.useCallback((value, choice) => {
        if (choice.confirmModal && value) {
            setModalConfirm({
                title: choice.confirmModal.title,
                content: choice.confirmModal.content,
                key: choice.key
            });
        } else {
            setPaymentForm(choice.key, value);
        }
    }, []);

    function MorePayments({ paymentChoices, paymentForm, toggleChange, setShowMorePayments, showMorePayments }) {
        return (
            <OptionalSection open={showMorePayments} onToggle={open => setShowMorePayments(open)} label={t('paymentinvoicing.payment_card.more_payments.title')}>
                <Section>
                    {paymentChoices.map(choice => (
                        <ToggleCard
                            key={choice.key}
                            toggle={paymentForm[choice.key]}
                            title={choice.title}
                            subtitle={choice.subtitle}
                            logoURL={choice.logo}
                            onToggle={value => toggleChange(value, choice)}
                            disabled={!paymentForm['paiement_online'] ? true : Boolean(choice.disabled)}
                        />
                    ))}
                </Section>
            </OptionalSection>
        );
    }

    const onCloseModal = React.useCallback(() => {
        setPaymentForm(modalConfirm.key, false);
        setModalConfirm(null);
    }, [modalConfirm, setPaymentForm]);

    const onToggleOnlinePayment = React.useCallback(
        value => {
            let form = {
                paiement_online: value,
                ...(!value && {
                    accepte_amex: false,
                    sofort_enabled: false,
                    paypal_enabled: false,
                    klarna_enabled: false,
                    paiement_offline: true
                })
            };
            setPaymentFormObject(form, true);
        },
        [setPaymentFormObject]
    );

    return (
        <>
            <WeezHeader
                title={t('paymentinvoicing.main_title')}
                rightComponent={[
                    <Button
                        primary
                        icon={<Icon className={Style['cta-save-icon']} name="save" margin={false} />}
                        key={0}
                        label={t('ticket.common.action.save')}
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                    />
                ]}
            >
                {t('paymentinvoicing.main_subtitle')}
            </WeezHeader>
            <ConfirmModal
                objectToSave={true}
                open={modalConfirm}
                onAccept={() => {
                    setPaymentForm(modalConfirm.key, true);
                    setModalConfirm(null);
                }}
                onClose={onCloseModal}
                labelTitle={modalConfirm?.title}
                labelBody={modalConfirm?.content}
            />
            <Card.FormStep
                title={t('paymentinvoicing.main_card_title')}
                rightComponent={
                    <Message title={t('paymentinvoicing.tips_box.title')} mini>
                        <p>{t('paymentinvoicing.tips_box.tips_1')}</p>
                        <p>{t('paymentinvoicing.tips_box.tips_2')}</p>
                        <p>{t('paymentinvoicing.tips_box.tips_3')}</p>
                        <p>{t('paymentinvoicing.tips_box.tips_4')}</p>
                    </Message>
                }
            >
                <>
                    <ToggleCard
                        className={Style['payment-toggle']}
                        title={t('paymentinvoicing.payment_card.toggle_enable_online_payments')}
                        subtitle={t('paymentinvoicing.payment_card.toggle_enable_online_payments_help')}
                        logoURL={[Visa, MasterCard]}
                        toggle={paymentForm['paiement_online']}
                        onToggle={onToggleOnlinePayment}
                        extraComponent={
                            <MorePayments
                                paymentChoices={filteredPaymentChoices}
                                paymentForm={paymentForm}
                                toggleChange={toggleChange}
                                showMorePayments={showMorePayments}
                                setShowMorePayments={setShowMorePayments}
                            />
                        }
                    />
                    <ToggleContainer
                        className={Style['classic-toggle']}
                        toggle={paymentForm['paiement_offline']}
                        title={
                            <>
                                <span>{t('paymentinvoicing.payment_card.toggle_reservations_accepted')}</span>
                                <TooltipHelp triggerOnHover title={t('paymentinvoicing.payment_card.toggle_reservations_accepted_tooltip')} />
                            </>
                        }
                        onToggle={value => setPaymentForm('paiement_offline', value)}
                    >
                        <Section>
                            <Subsection>
                                <TextAreaLabel
                                    label={t('paymentinvoicing.payment_card.toggle_reservations_instructions_title')}
                                    placeholder={t('paymentinvoicing.payment_card.toggle_reservations_instructions_placeholder')}
                                    value={paymentForm.instructions_paiement_offline}
                                    onChange={({ target: { value } }) => {
                                        setPaymentForm('instructions_paiement_offline', value);
                                    }}
                                    style={{ resize: 'none' }}
                                />
                            </Subsection>
                        </Section>
                    </ToggleContainer>
                    <ToggleContainer
                        className={Style['classic-toggle']}
                        toggle={paymentForm['accepte_remboursement']}
                        title={
                            <>
                                <span>{t('paymentinvoicing.payment_card.toggle_refund_accepted')}</span>
                                <TooltipHelp triggerOnHover title={t('paymentinvoicing.payment_card.toggle_refund_accepted_tooltip')} />
                            </>
                        }
                        onToggle={value => setPaymentForm('accepte_remboursement', value)}
                    >
                        <Section>
                            <Subsection>
                                <TextAreaLabel
                                    label={t('paymentinvoicing.payment_card.toggle_refund_title')}
                                    placeholder={t('paymentinvoicing.payment_card.toggle_reservations_instructions_placeholder')}
                                    value={paymentForm.instructions_remboursement}
                                    onChange={({ target: { value } }) => {
                                        setPaymentForm('instructions_remboursement', value);
                                    }}
                                    style={{ resize: 'none' }}
                                />
                            </Subsection>
                        </Section>
                    </ToggleContainer>
                    <ToggleContainer
                        className={Style['classic-toggle']}
                        toggle={Boolean(paymentForm['limit_tickets_per_order'])}
                        onToggle={value => setPaymentForm('limit_tickets_per_order', value ? 1 : 0)}
                        title={
                            <>
                                <span>{t('paymentinvoicing.payment_card.toggle_ticket_number_maximum_per_order')}</span>
                                <TooltipHelp triggerOnHover title={t('paymentinvoicing.payment_card.toggle_ticket_number_maximum_per_order_tooltip')} />
                            </>
                        }
                    >
                        <Counter
                            onChange={(_, value) => {
                                setPaymentForm('limit_tickets_per_order', value);
                            }}
                            value={paymentForm['limit_tickets_per_order']}
                            min={1}
                            max={999}
                            step={1}
                        />
                    </ToggleContainer>
                </>
            </Card.FormStep>
            <Card.FormStep title={t('paymentinvoicing.invoice_card.main_title')} rightComponent>
                <>
                    <div className={Style['invoicing-selector']}>
                        <SelectLabel
                            label={
                                <>
                                    <span>{t('paymentinvoicing.invoice_card.select_box.label')}</span>
                                    <TooltipHelp triggerOnHover title={t('paymentinvoicing.invoice_card.select_box.label_tooltip')} />
                                </>
                            }
                            value={parseInt(invoicingForm.etat)}
                            onChange={value => {
                                setInvoiceForm('etat', value);
                            }}
                            options={[
                                {
                                    text: t('paymentinvoicing.invoice_card.select_box.choice_1'),
                                    value: 0,
                                    key: 'deactivate'
                                },
                                {
                                    text: t('paymentinvoicing.invoice_card.select_box.choice_2'),
                                    value: 1,
                                    key: 'optionnal'
                                },
                                {
                                    text: t('paymentinvoicing.invoice_card.select_box.choice_3'),
                                    value: 2,
                                    key: 'mandatory'
                                }
                            ]}
                        ></SelectLabel>
                    </div>
                    <ToggleContainer
                        toggle={parseInt(invoicingForm.etat_hl) === 1}
                        className={Style['toggle-container-custom']}
                        title={
                            <>
                                <span>{t('paymentinvoicing.invoice_card.specifics_coordinates.toggle')}</span>
                                <TooltipHelp triggerOnHover title={t('paymentinvoicing.invoice_card.specifics_coordinates.toggle_tooltip')} />
                            </>
                        }
                        onToggle={value => {
                            setInvoiceForm('etat_hl', value ? 1 : 0);
                        }}
                    >
                        <Section className={Style['specifics-coordinates-form']}>
                            <Message visible={true} type="default" title="">
                                {t('paymentinvoicing.invoice_card.specifics_coordinates.message')}
                            </Message>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_1')} />
                            <Input
                                value={invoicingForm.societe}
                                onChange={e => {
                                    setInvoiceForm('societe', e.target.value);
                                }}
                            ></Input>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_2')} />
                            <Input
                                value={invoicingForm.forme}
                                onChange={e => {
                                    setInvoiceForm('forme', e.target.value);
                                }}
                            ></Input>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_3')} />
                            <Input
                                value={invoicingForm.siret}
                                onChange={e => {
                                    setInvoiceForm('siret', e.target.value);
                                }}
                            ></Input>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_4')} />
                            <Input
                                value={invoicingForm.tva_intra}
                                onChange={e => {
                                    setInvoiceForm('tva_intra', e.target.value);
                                }}
                            ></Input>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_5')} />
                            <Input
                                value={invoicingForm.siret_lieu}
                                onChange={e => {
                                    setInvoiceForm('siret_lieu', e.target.value);
                                }}
                            ></Input>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_6')} />
                            <Input
                                value={invoicingForm.adresse}
                                onChange={e => {
                                    setInvoiceForm('adresse', e.target.value);
                                }}
                            ></Input>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_7')} />
                            <Input
                                value={invoicingForm.cp}
                                onChange={e => {
                                    setInvoiceForm('cp', e.target.value);
                                }}
                            ></Input>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_8')} />
                            <Input
                                value={invoicingForm.ville}
                                onChange={e => {
                                    setInvoiceForm('ville', e.target.value);
                                }}
                            ></Input>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_9')} />
                            <Input
                                value={invoicingForm.pays}
                                onChange={e => {
                                    setInvoiceForm('pays', e.target.value);
                                }}
                            ></Input>
                            <Form.Label label={t('paymentinvoicing.invoice_card.specifics_coordinates.form.label_10')} />
                            <Input
                                value={invoicingForm.divers}
                                onChange={e => {
                                    setInvoiceForm('divers', e.target.value);
                                }}
                            ></Input>
                        </Section>
                    </ToggleContainer>
                    <ToggleContainer
                        className={Style['toggle-container-custom']}
                        toggle={paymentForm['proforma_enabled']}
                        title={
                            <>
                                <span>{t('paymentinvoicing.invoice_card.proforma.toggle')}</span>
                                <TooltipHelp triggerOnHover title={t('paymentinvoicing.invoice_card.proforma.toggle_tooltip')} />
                            </>
                        }
                        onToggle={value => setPaymentForm('proforma_enabled', value)}
                    ></ToggleContainer>
                </>
            </Card.FormStep>
        </>
    );
}
